'use client';

import { __spreadArray } from '../../_virtual/_tslib.js';
import React__default, { forwardRef, useState, useRef, useEffect, useCallback, useImperativeHandle } from 'react';
import getAdaptiveHeightList from '../helpers/getAdaptiveHeightList.js';
import { quickFilterByKeywords } from '../../Utils/helpers.js';
import 'react-dom';
import '../../hooks/useMakeSearchable/types.js';
import '../../Utils/constants.js';
import '../../services/resumes/types.js';
import useKeyboardAccessibility from '../../hooks/useKeyboardAccessibility.js';
import { MultiLevelCoreContainer, ListWrapper, List, Item, ItemWrapper } from './styles.js';

var LIST_ID = 'multi-level-list-';
var MultiLevelCore = forwardRef(function (_a, ref) {
    var _b;
    var enableKeyboardNavigation = _a.enableKeyboardNavigation, onClickEscape = _a.onClickEscape, values = _a.values, maxHeight = _a.maxHeight, options = _a.options, renderItem = _a.renderItem, fieldValue = _a.fieldValue, fieldKey = _a.fieldKey, onChange = _a.onChange, _c = _a.adaptiveHeight, adaptiveHeight = _c === void 0 ? true : _c, _d = _a.searchKeywords, searchKeywords = _d === void 0 ? '' : _d, onClearSearchKeywords = _a.onClearSearchKeywords, onClickLeafItem = _a.onClickLeafItem, emptyNode = _a.emptyNode, onChangeTitle = _a.onChangeTitle, onShowBackButton = _a.onShowBackButton;
    /**
     * State
     * */
    var _e = useState([]), selectedValues = _e[0], setSelectedValues = _e[1];
    var _f = useState([options]), multiOptions = _f[0], setMultiOptions = _f[1];
    var _g = useState([]), adaptiveHeightList = _g[0], setAdaptiveHeightList = _g[1];
    var _h = useState(0), level = _h[0], setLevel = _h[1];
    /**
     * Ref
     * */
    var containerRef = useRef(null);
    var listWrapperRef = useRef(null);
    var cachedMultiDropdowns = useRef([options]);
    var canSetInitialValues = useRef(true);
    /**
     * Effects
     * */
    // Set values
    useEffect(function () {
        /**
         * Only set selected values in first mount
         * this avoid selectedValues being set after onChange values
         * because the values after onChange may be missing
         * */
        if (canSetInitialValues.current && values) {
            setSelectedValues(values);
            canSetInitialValues.current = false;
        }
    }, [values]);
    // Set dropdown title
    useEffect(function () {
        var displayLevel = level - 1;
        var displayValue = selectedValues[displayLevel];
        if (displayValue) {
            var titleToDisplay = displayValue[fieldValue];
            onChangeTitle(titleToDisplay);
        }
        else {
            onChangeTitle('');
        }
    }, [selectedValues, level, fieldValue]);
    // Set initial options
    useEffect(function () {
        if (canSetInitialValues.current && options) {
            setMultiOptions([options]);
            canSetInitialValues.current = false;
        }
    }, [options]);
    // Transition to next level
    useEffect(function () {
        if (containerRef.current && listWrapperRef.current) {
            var transitionWidth = containerRef.current.offsetWidth * level;
            listWrapperRef.current.style.transform = "translateX(-".concat(transitionWidth, "px)");
            if (adaptiveHeight && maxHeight) {
                var height = adaptiveHeightList[level];
                if (height > maxHeight) {
                    containerRef.current.style.height = maxHeight + 'px';
                    containerRef.current.style.overflowY = 'auto';
                }
                else {
                    containerRef.current.style.height = height + 'px';
                    containerRef.current.style.overflowY = 'hidden';
                }
            }
        }
    }, [level, adaptiveHeightList, adaptiveHeight, maxHeight]);
    // Filter options by keyword
    useEffect(function () {
        if (level < 0) {
            return;
        }
        var changedMultiOptionsByKeywords = quickFilterByKeywords(cachedMultiDropdowns.current[level], searchKeywords, fieldValue);
        var changedMultiOptions = __spreadArray([], cachedMultiDropdowns.current, true);
        changedMultiOptions[level] = changedMultiOptionsByKeywords;
        setMultiOptions(changedMultiOptions);
    }, [level, searchKeywords, fieldValue, fieldKey]);
    // Adaptive height
    useEffect(function () {
        if (listWrapperRef.current) {
            var adaptiveHeight_1 = getAdaptiveHeightList(listWrapperRef.current);
            setAdaptiveHeightList(adaptiveHeight_1);
        }
    }, [multiOptions, maxHeight]);
    // Show back button
    useEffect(function () {
        if (level > 0) {
            onShowBackButton(true);
        }
        else {
            onShowBackButton(false);
        }
    }, [level]);
    /**
     * Render
     * */
    var dropdownWidth = ((_b = containerRef.current) === null || _b === void 0 ? void 0 : _b.offsetWidth) || 0;
    /**
     * Logic
     * */
    var handleBack = useCallback(function () {
        onClearSearchKeywords && onClearSearchKeywords();
        setLevel(function (prevLevel) {
            if (prevLevel === 0) {
                return prevLevel;
            }
            var changedLevel = prevLevel - 1;
            return changedLevel;
        });
        /**
         * Scroll to top when back
         * If QC want to scroll to selected values, create a cached to save selected value positions
         * But might do it later if asked
         * */
        if (containerRef.current) {
            containerRef.current.scrollTop = 0;
        }
    }, [onClearSearchKeywords]);
    var handleSelectItem = useCallback(function (option, index) {
        var _a;
        /**
         * Set multi options
         * */
        setMultiOptions(function (prevMultiOption) {
            if (option.children) {
                var changedMultiOptions = __spreadArray([], prevMultiOption, true);
                changedMultiOptions[index + 1] = option.children;
                cachedMultiDropdowns.current[index + 1] = option.children;
                return changedMultiOptions;
            }
            return prevMultiOption;
        });
        /**
         * Set multi values
         * */
        var valuesLength = selectedValues.length - 1;
        var changedValues = __spreadArray([], selectedValues, true);
        var inputValues = (_a = {}, _a[fieldKey] = option[fieldKey], _a[fieldValue] = option[fieldValue], _a);
        /**
         * Rules:
         *  0: If selected value is already containt in selectedValues, don't do anything
         *  1: If index is greater than values's length -> Push new value
         *  2: If index is less or equal than values's length -> replace item's value of index position and remove all value behind
         * */
        var selectedValuesIds = changedValues.map(function (value) { return value[fieldValue]; });
        if (!selectedValuesIds.includes(option[fieldValue])) {
            switch (true) {
                case index > valuesLength: {
                    changedValues.push(inputValues);
                    break;
                }
                case index <= valuesLength: {
                    changedValues = changedValues.slice(0, index);
                    changedValues.push(inputValues);
                    break;
                }
            }
        }
        // Clear keywords before set active level
        onClearSearchKeywords && onClearSearchKeywords();
        setSelectedValues(changedValues);
        if (option.children && option.children.length > 0) {
            setLevel(index + 1);
            return;
        }
        /**
         * Click leaf item
         * */
        onClickLeafItem();
        setLevel(0);
        onChange(changedValues);
    }, [fieldKey, fieldValue, onChange, onClearSearchKeywords, onClickLeafItem, selectedValues]);
    /**
     * Hooks
     * */
    // Keyboard navigation
    var activeIndex = useKeyboardAccessibility(enableKeyboardNavigation, {
        target: "#".concat(LIST_ID + level),
        optionsLength: multiOptions[level].length,
        onClickEscape: onClickEscape,
        forceResetIndexDeps: [level, searchKeywords],
        onSelelected: function (index) {
            var selectedOption = multiOptions[level][index];
            handleSelectItem(selectedOption, level);
        }
    }).activeIndex;
    useImperativeHandle(ref, function () { return ({
        back: handleBack
    }); });
    return (React__default.createElement(MultiLevelCoreContainer, { ref: containerRef },
        React__default.createElement(ListWrapper, { maxHeight: maxHeight, ref: listWrapperRef }, multiOptions.map(function (options, multiOptionsIndex) {
            // Render list
            var isActiveList = multiOptionsIndex === level;
            return (React__default.createElement(List, { key: LIST_ID + multiOptionsIndex, id: LIST_ID + multiOptionsIndex, width: dropdownWidth, isActive: isActiveList, style: {
                    height: !isActiveList ? 0 : undefined,
                    overflow: !isActiveList ? 'hidden' : undefined
                } }, options.length > 0 ? (options.map(function (option, index) {
                var valueIds = selectedValues.map(function (item) { return item[fieldValue]; });
                // Render item
                return (React__default.createElement(Item, { key: option[fieldKey] },
                    React__default.createElement(ItemWrapper, { role: 'button', onClick: function () { return handleSelectItem(option, multiOptionsIndex); } }, renderItem({
                        displayValue: option[fieldValue],
                        isSelected: valueIds.includes(option[fieldValue]),
                        isHaveChild: !!option.children,
                        isHover: index === activeIndex
                    }))));
            })) : (React__default.createElement(React__default.Fragment, null, emptyNode))));
        }))));
});

export { MultiLevelCore as default };
//# sourceMappingURL=index.js.map
